import type { ContractTemplateVersionView, ContractTemplateView } from "@closer/shared";
import { CONTRACT_TEMPLATE_STATUS_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  DefinitionGrid,
  Input,
  PageHeader,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ŞABLON DETAYI (CLOS-125 `[107]`).
 *
 * ⚠ Ekran şablonun METNİNİ göstermiyor, KİMLİĞİNİ gösteriyor: ad, tür, durum, gövde
 * biçimi, madde sayısı. Metin düzenleme `[108]`'in kendi ekranı; buraya salt okunur bir
 * metin dökümü koysaydık iki ekran aynı içeriği iki farklı biçimde sunar ve biri gün gelip
 * geride kalırdı.
 *
 * ⚠ Erişim kararı SUNUCUDA: yetkisiz kullanıcı 403, başka çalışma alanının şablonu 404.
 * İkisi de aynı güvenli duruma düşüyor — "var ama sana kapalı" demek kaydın varlığını
 * doğrulamak olurdu.
 *
 * ### UI Tur 2 §7.14 — ATLANANLAR
 *
 * Spec sağda 280px `SummaryRail` (Kategori / Aktif Versiyon / Son Güncelleme / Oluşturan)
 * istiyor; dördünden biri (Oluşturan) zaten `ContractTemplateView`'da var ama diğer üçü
 * yok — "Kategori" diye ayrı bir alan yok (yalnız serbest metin `contractType`), semantik
 * bir sürüm dizesi ("v1.3") yok, `updatedAt` hiç gelmiyor. Üç alanı boş bir rayla çizmek
 * ADR-0014 ihlali olurdu; rota ÇİZİLMİYOR, kimlik bilgisi aşağıdaki `DefinitionGrid`'de
 * (var olan alanlarla) kalıyor.
 */

export function TemplateDetailPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kopyaAcik, setKopyaAcik] = useState(false);
  const [kopyaAdi, setKopyaAdi] = useState("");
  const [cakisma, setCakisma] = useState<string | null>(null);
  const [cakismaOnayi, setCakismaOnayi] = useState(false);

  /**
   * ÇOĞALTMA (CLOS-127 `[109]`). Yeni ad ZORUNLU olduğu için düğme boş adla kapalı; adı
   * uydursaydık kütüphanede kimin ne için açtığı belirsiz satırlar birikirdi.
   */
  const cogalt = useMutation({
    mutationFn: async () =>
      (
        await api<ContractTemplateView>(`/api/templates/${id}/copy`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({
            name: kopyaAdi.trim(),
            acknowledgeDuplicateName: cakismaOnayi,
          }),
        })
      ).data,
    onSuccess: (kopya) => {
      toast.success("Şablon çoğaltıldı");
      void navigate(`/app/sablonlar/${kopya.id}`);
    },
    onError: (hata) => {
      if (hata instanceof ApiRequestError && hata.fields?.[0]?.code === "duplicate_name") {
        setCakisma(hata.message);
        setCakismaOnayi(true);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Şablon çoğaltılamadı");
    },
  });

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "template", id),
    queryFn: async () =>
      (await api<ContractTemplateView>(`/api/templates/${id}`, { tenantId })).data,
  });

  /**
   * SÜRÜM GEÇMİŞİ (CLOS-1117 · yetim uç G). `GET …/versions` sunucuda vardı, hiçbir ekran
   * çağırmıyordu: *"önceki içerik kaybolmaz"* kriterinin kullanıcıya görünen yüzü yalnız
   * "Düzenleme sayısı" rakamıydı — kim, ne zaman değiştirdi okunamıyordu.
   *
   * ⚠ Anahtar `template/<id>` ÖN EKİNİN ALTINDA: düzenleme ve arşiv sonrası tekil şablon
   * tazelenince geçmiş de tazeleniyor (`TemplateEditPage` bunu açıkça da yazıyor).
   *
   * ⚠ DETAY GELMEDEN KOŞMUYOR (`enabled`): erişimsiz kullanıcıda detay 404 verirken ikinci
   * bir 404 üretmenin anlamı yok — iki uç sunucuda aynı kapıdan geçiyor.
   */
  const surumler = useQuery({
    queryKey: tenantKey(tenantId, "template", id, "surumler"),
    queryFn: async () =>
      (
        await api<ContractTemplateVersionView[]>(`/api/templates/${id}/versions`, {
          tenantId,
        })
      ).data,
    enabled: sorgu.isSuccess,
  });

  /** Arşivleme ve geri alma AYNI uçtan değil ama aynı düğmeden: yön duruma göre. */
  const arsiv = useMutation({
    mutationFn: async (arsivle: boolean) =>
      api<ContractTemplateView>(`/api/templates/${id}/${arsivle ? "archive" : "reactivate"}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      /**
       * ⚠ LİSTE de tazeleniyor (kusur avı, 26 Ağustos). Arşivlenen şablon hem şablon
       * listesinden hem de "Yeni Sözleşme" merkezinin seçiminden düşmeli; tekil anahtar
       * (`template`) çoğulu (`templates`) kapsamıyor. Tazelemeyince kullanıcı arşivlediği
       * şablonu seçim ekranında görmeye devam ediyor ve seçtiğinde sunucudan ret alıyordu.
       */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "template", id) });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "templates") });
      toast.success("Şablon durumu güncellendi");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Durum değiştirilemedi"),
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Şablon yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Şablon bulunamadı",
                description: "Bu şablon yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
        <Link className="text-sm font-medium text-primary hover:underline" to="/app/sablonlar">
          Şablonlara dön
        </Link>
      </div>
    );
  }

  const sablon = sorgu.data;

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title={sablon.name}
        meta={
          <Badge tone={sablon.status === "active" ? "success" : "neutral"}>
            {CONTRACT_TEMPLATE_STATUS_NAMES[sablon.status]}
          </Badge>
        }
        actions={
          <>
            {/*
              ⚠ Düzenle düğmesi SİSTEM şablonunda hiç çıkmıyor (CLOS-126 `[108]`): basınca
              403 alacak bir düğme, kullanıcıya sebebi okumadan denemeyi öğretirdi.
            */}
            {/*
              ⚠ ÇOĞALTMA sistem şablonunda DA açık (CLOS-127 `[109]`): `[108]` düzenlemeyi
              kapatırken kullanıcıya "kendi kopyanızı oluşturun" diyor ve bu, o çıkış yolu.
            */}
            {can("template.create") && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setKopyaAcik(true)}
              >
                Çoğalt
              </Button>
            )}
            {/*
              ARŞİVLEME (CLOS-128 `[110]`) — kendi yetkisiyle. Arşiv, şablonu geçmişini
              silmeden kütüphaneden çıkarıyor; sistem şablonunda hiç sunulmuyor.
            */}
            {can("template.archive") && !sablon.isSystem && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={arsiv.isPending}
                onClick={() => arsiv.mutate(sablon.status === "active")}
              >
                {sablon.status === "active" ? "Arşivle" : "Yeniden aktifleştir"}
              </Button>
            )}
            {can("template.edit") && !sablon.isSystem && (
              <Link to={`/app/sablonlar/${sablon.id}/duzenle`}>
                <Button type="button" variant="secondary" size="sm">
                  Düzenle
                </Button>
              </Link>
            )}
          </>
        }
      />

      {kopyaAcik && (
        <Card title="Şablonu çoğalt">
          <form
            noValidate
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              cogalt.mutate();
            }}
          >
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-fg-soft">Yeni şablon adı</span>
              <Input
                value={kopyaAdi}
                onChange={(e) => setKopyaAdi(e.target.value)}
                placeholder={`${sablon.name} — kopya`}
              />
            </label>
            <Button
              type="submit"
              loading={cogalt.isPending}
              disabled={kopyaAdi.trim().length === 0}
            >
              Kopyala
            </Button>
            <Button type="button" variant="secondary" onClick={() => setKopyaAcik(false)}>
              Vazgeç
            </Button>
          </form>
          {/* ⚠ Ad çakışması UYARI: kullanıcı onaylarsa aynı adla kopya oluşuyor. */}
          {cakisma && (
            <p role="alert" className="pt-2 text-sm text-warning">
              {cakisma} Kopyala düğmesine yeniden basarsanız aynı adla oluşturulur.
            </p>
          )}
        </Card>
      )}

      <Card padding="default">
        <DefinitionGrid
          columns={3}
          items={[
            { key: "aciklama", label: "Açıklama", value: sablon.description ?? "—" },
            { key: "tur", label: "Sözleşme türü", value: sablon.contractType ?? "Her tür" },
            {
              key: "govde",
              label: "Gövde",
              value: sablon.bodyKind === "file" ? "Dosya tabanlı" : "Uygulama içi metin",
            },
            sablon.bodyKind === "file"
              ? { key: "dosya", label: "Dosya", value: sablon.fileName ?? "—" }
              : {
                  key: "madde",
                  label: "Madde sayısı",
                  value: sablon.clauseCount === null ? "—" : String(sablon.clauseCount),
                },
            { key: "olusturan", label: "Oluşturan", value: sablon.createdByName },
            /** Kaç kez düzenlendiği görünür: "önceki içerik kaybolmaz" kriterinin yüzü. */
            { key: "revizyon", label: "Düzenleme sayısı", value: String(sablon.revisionCount) },
            ...(sablon.isSystem
              ? [{ key: "kaynak", label: "Kaynak", value: "Sistem şablonu" }]
              : []),
            {
              key: "olusturma-tarihi",
              label: "Oluşturma tarihi",
              value: new Date(sablon.createdAt).toLocaleString("tr-TR"),
            },
          ]}
        />
      </Card>

      {/*
        ⚠ Üç durum AYRI (BillingPage dersi): "geçmiş yok" ile "geçmiş GELMEDİ" aynı cümle
        olamaz — ilki kullanıcıya "henüz düzenlenmedi" der, ikincisi bir arızayı gizlerdi.
      */}
      <Card title="Sürüm geçmişi">
        {surumler.isPending ? (
          <div role="status" aria-label="Sürüm geçmişi yükleniyor">
            <Skeleton lines={3} />
          </div>
        ) : surumler.error ? (
          <StateView
            state={resolveErrorState(surumler.error, { idempotent: true })}
            onRetry={() => void surumler.refetch()}
          />
        ) : surumler.data.length === 0 ? (
          <p className="text-sm text-fg-soft">
            Henüz önceki bir sürüm yok; ilk düzenlemeyle birlikte burada listelenir.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Sürüm geçmişi">
            {surumler.data.map((surum) => (
              <li
                key={surum.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm"
              >
                <span className="font-medium text-fg-strong">Sürüm {surum.versionNumber}</span>
                <span className="text-fg-muted">
                  {new Date(surum.createdAt).toLocaleString("tr-TR")}
                </span>
                <span className="text-fg-muted">{surum.changedByName}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Link className="text-sm font-medium text-primary hover:underline" to="/app/sablonlar">
        Şablonlara dön
      </Link>
    </div>
  );
}
